import { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

interface Props {
  birthDate: string;
  dayStem: string;
  year: number;
}

const EVENT_TYPES = [
  { id: 'wedding', label: '결혼식 · 프로포즈', icon: 'ri-heart-3-line', color: 'text-rose-400', bg: 'bg-rose-500/10 border-rose-500/30' },
  { id: 'interview', label: '중요 면접 · 발표', icon: 'ri-briefcase-4-line', color: 'text-sky-400', bg: 'bg-sky-500/10 border-sky-500/30' },
  { id: 'contract', label: '계약 · 사업 개시', icon: 'ri-file-text-line', color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/30' },
  { id: 'moving', label: '이사 · 개업', icon: 'ri-home-4-line', color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/30' },
  { id: 'exam', label: '시험 · 자격증', icon: 'ri-graduation-cap-line', color: 'text-violet-400', bg: 'bg-violet-500/10 border-violet-500/30' },
  { id: 'meeting', label: '첫 만남 · 맞선', icon: 'ri-user-smile-line', color: 'text-pink-400', bg: 'bg-pink-500/10 border-pink-500/30' },
  { id: 'investment', label: '투자 · 부동산 계약', icon: 'ri-money-dollar-circle-line', color: 'text-yellow-400', bg: 'bg-yellow-500/10 border-yellow-500/30' },
  { id: 'travel', label: '해외 출장 · 여행', icon: 'ri-plane-line', color: 'text-cyan-400', bg: 'bg-cyan-500/10 border-cyan-500/30' },
] as const;

type EventType = typeof EVENT_TYPES[number]['id'];

const MONTH_LABELS = ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'];

// Day-level element by day number in month (cyclical, approximated)
const DAY_ELEMENTS = ['목', '목', '화', '화', '토', '금', '금', '수', '수', '토', '목', '목', '화', '화', '토', '금', '금', '수', '수', '토', '목', '목', '화', '화', '토', '금', '금', '수', '수', '토', '목'];

// Element compatibility
const ELEMENT_COMPAT: Record<string, Record<string, number>> = {
  목: { 목: 3, 화: 5, 토: 2, 금: 1, 수: 4 },
  화: { 목: 4, 화: 3, 토: 5, 금: 2, 수: 1 },
  토: { 목: 2, 화: 4, 토: 3, 금: 5, 수: 1 },
  금: { 목: 2, 화: 1, 토: 4, 금: 3, 수: 5 },
  수: { 목: 5, 화: 2, 토: 1, 금: 4, 수: 3 },
};

// Event type boosts specific elements
const EVENT_ELEMENT_BOOST: Record<EventType, string[]> = {
  wedding: ['화', '토'],
  interview: ['금', '수'],
  contract: ['토', '금'],
  moving: ['목', '수'],
  exam: ['수', '목'],
  meeting: ['화', '목'],
  investment: ['토', '금'],
  travel: ['수', '화'],
};

// Event advice texts
const EVENT_ADVICE: Record<EventType, string[]> = {
  wedding: ['음양이 조화를 이루는 날, 부부 금슬이 깊어집니다', '화(火) 기운이 강해 따뜻하고 밝은 시작을 선물합니다', '귀인의 복이 깃든 날, 평생의 동반자를 맞이하기 좋습니다'],
  interview: ['금(金)의 날카로운 판단력이 빛나는 날입니다', '수(水)의 지혜가 면접관의 마음을 사로잡을 것입니다', '당신의 진가를 가장 잘 발휘할 수 있는 최적의 날입니다'],
  contract: ['토(土)의 안정된 기운이 계약의 신뢰를 높입니다', '금(金)의 결단력으로 유리한 조건을 끌어낼 수 있습니다', '이 날 맺은 약속은 단단하고 오래 지속됩니다'],
  moving: ['새로운 터전에 복이 깃드는 대길일입니다', '목(木)의 생장 기운이 새 공간에 활기를 불어넣습니다', '이사 방향과 시간까지 맞추면 효과가 배가됩니다'],
  exam: ['수(水)의 지혜와 집중력이 최고조에 달하는 날입니다', '실력을 100% 발휘할 수 있는 컨디션의 날입니다', '긴장보다 여유로운 마음이 좋은 결과를 만들 것입니다'],
  meeting: ['첫인상이 좋게 남는 길상(吉相)의 날입니다', '화(火) 기운으로 매력이 넘치고 대화가 자연스럽게 흐릅니다', '운명적 인연을 만날 수 있는 특별한 날입니다'],
  investment: ['재성(財星)이 강하게 활성화되는 재물의 날입니다', '토(土)의 안정성이 투자의 기반을 탄탄하게 합니다', '결단의 타이밍을 놓치지 마세요'],
  travel: ['수(水)의 유연한 흐름으로 여정이 순탄합니다', '새로운 곳에서 귀한 인연과 기회를 만나게 됩니다', '이동과 변화에 가장 좋은 기운이 흐르는 날입니다'],
};

const SCORE_CONFIG: Record<number, { label: string; color: string; bg: string; bar: string }> = {
  5: { label: '대길(大吉)', color: 'text-amber-400', bg: 'bg-amber-500/15 border-amber-500/40', bar: 'bg-amber-400' },
  4: { label: '길(吉)', color: 'text-emerald-400', bg: 'bg-emerald-500/15 border-emerald-500/35', bar: 'bg-emerald-400' },
  3: { label: '평길(平吉)', color: 'text-sky-300', bg: 'bg-sky-500/10 border-sky-500/30', bar: 'bg-sky-400' },
};

const DAY_STEM_ELEMENT: Record<string, string> = { 갑: '목', 을: '목', 병: '화', 정: '화', 무: '토', 기: '토', 경: '금', 신: '금', 임: '수', 계: '수' };

interface LuckyDay {
  day: number;
  date: string;
  weekday: string;
  score: number;
  elementOfDay: string;
  advice: string;
}

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

function generateLuckyDays(birthDate: string, dayStem: string, year: number, month: number, eventType: EventType): LuckyDay[] {
  const seed = birthDate.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  const userEl = DAY_STEM_ELEMENT[dayStem] ?? '목';
  const boostedElements = EVENT_ELEMENT_BOOST[eventType];
  const totalDays = getDaysInMonth(year, month);
  const advicePool = EVENT_ADVICE[eventType];

  const days: LuckyDay[] = [];
  for (let d = 1; d <= totalDays; d++) {
    const dayEl = DAY_ELEMENTS[(d - 1) % DAY_ELEMENTS.length];
    const baseCompat = ELEMENT_COMPAT[userEl]?.[dayEl] ?? 3;
    const eventBoost = boostedElements.includes(dayEl) ? 1 : 0;
    const personalSeed = ((seed + d * 7 + month * 13) % 3);
    const rawScore = Math.min(5, Math.max(1, baseCompat + eventBoost - personalSeed + (d % 3 === 0 ? 1 : 0)));

    if (rawScore >= 3) {
      const dateObj = new Date(year, month - 1, d);
      const weekday = WEEKDAYS[dateObj.getDay()];
      const adviceIdx = (seed + d + month) % advicePool.length;
      days.push({
        day: d,
        date: `${year}년 ${month}월 ${d}일`,
        weekday,
        score: rawScore,
        elementOfDay: dayEl,
        advice: advicePool[adviceIdx],
      });
    }
  }

  return days.sort((a, b) => b.score - a.score).slice(0, 7);
}

export default function LuckyDayCalculator({ birthDate, dayStem, year }: Props) {
  const [searchParams] = useSearchParams();
  const [selectedEventType, setSelectedEventType] = useState<EventType | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth() + 1);
  const [eventLabel, setEventLabel] = useState('');
  const [isCalculating, setIsCalculating] = useState(false);
  const [results, setResults] = useState<LuckyDay[] | null>(null);
  const [highlightSection, setHighlightSection] = useState(false);

  // Auto-select event type and month from URL params
  useEffect(() => {
    const eventParam = searchParams.get('event') as EventType | null;
    const monthParam = parseInt(searchParams.get('month') ?? '', 10);

    if (eventParam && EVENT_TYPES.some((e) => e.id === eventParam)) {
      setSelectedEventType(eventParam);
      setResults(null);
      setHighlightSection(true);
      setTimeout(() => setHighlightSection(false), 2500);
    }
    if (!isNaN(monthParam) && monthParam >= 1 && monthParam <= 12) {
      setSelectedMonth(monthParam);
    }
  }, [searchParams]);

  const currentEventConfig = EVENT_TYPES.find((e) => e.id === selectedEventType);

  const luckyDays = useMemo(() => {
    if (!selectedEventType) return null;
    return generateLuckyDays(birthDate, dayStem, year, selectedMonth, selectedEventType);
  }, [birthDate, dayStem, year, selectedMonth, selectedEventType]);

  const handleCalculate = () => {
    if (!selectedEventType) return;
    setIsCalculating(true);
    setResults(null);
    setTimeout(() => {
      setIsCalculating(false);
      setResults(luckyDays);
    }, 1200);
  };

  return (
    <div className={`p-6 rounded-2xl transition-all duration-500 ${
      highlightSection
        ? 'bg-gradient-to-br from-primary-gold/15 to-amber-600/10 border-2 border-primary-gold/60'
        : 'bg-gradient-to-br from-primary-dark/80 to-mystic-dark/80 border border-primary-gold/20'
    }`}>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-amber-500/15 border border-amber-500/30 flex-shrink-0">
          <i className="ri-calendar-check-line text-amber-400 text-xl"></i>
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h2 className="text-white font-bold text-lg">개인 길일(吉日) 계산기</h2>
            {highlightSection && (
              <span className="px-2.5 py-0.5 rounded-full bg-primary-gold/20 border border-primary-gold/40 text-primary-gold text-xs font-bold animate-pulse">
                결정비서 연동
              </span>
            )}
          </div>
          <p className="text-purple-400/60 text-xs">중요한 날에 가장 좋은 날을 골라드립니다</p>
        </div>
      </div>

      {/* Preset notice from destiny mode */}
      {highlightSection && selectedEventType && currentEventConfig && (
        <div className="mb-4 px-4 py-3 rounded-xl bg-primary-gold/10 border border-primary-gold/30 flex items-center gap-3">
          <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
            <i className="ri-scales-3-line text-primary-gold text-sm"></i>
          </div>
          <p className="text-primary-gold/90 text-xs font-semibold">
            결정비서에서 연결됐어요 — <span className="text-white">{currentEventConfig.label}</span> 이벤트가 자동 선택됐습니다
          </p>
        </div>
      )}

      {/* Event type selection */}
      <div className="mb-5">
        <p className="text-purple-300/70 text-xs font-semibold uppercase tracking-wide mb-3">이벤트 유형 선택</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {EVENT_TYPES.map((ev) => (
            <button
              key={ev.id}
              onClick={() => { setSelectedEventType(ev.id); setResults(null); }}
              className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border transition-all text-left cursor-pointer ${
                selectedEventType === ev.id
                  ? ev.bg + ' scale-[1.02]'
                  : 'bg-white/5 border-white/10 hover:border-white/20'
              }`}
            >
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className={`${ev.icon} ${selectedEventType === ev.id ? ev.color : 'text-purple-400/60'} text-sm`}></i>
              </div>
              <span className={`text-xs font-medium leading-tight ${selectedEventType === ev.id ? 'text-white' : 'text-purple-300/70'}`}>{ev.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Month selector + optional label */}
      <div className="flex flex-col sm:flex-row gap-4 mb-5">
        <div className="flex-1">
          <p className="text-purple-300/70 text-xs font-semibold uppercase tracking-wide mb-2">희망 월(月)</p>
          <div className="flex flex-wrap gap-1.5">
            {MONTH_LABELS.map((label, idx) => {
              const m = idx + 1;
              const isPast = m < new Date().getMonth() + 1 && year === new Date().getFullYear();
              return (
                <button
                  key={m}
                  onClick={() => { if (!isPast) { setSelectedMonth(m); setResults(null); } }}
                  disabled={isPast}
                  className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap border ${
                    selectedMonth === m
                      ? 'bg-primary-gold text-black border-primary-gold'
                      : isPast
                      ? 'bg-white/2 border-white/5 text-purple-500/30 cursor-not-allowed'
                      : 'bg-white/5 border-white/10 text-purple-300 hover:border-primary-gold/40'
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
        <div className="sm:w-52">
          <p className="text-purple-300/70 text-xs font-semibold uppercase tracking-wide mb-2">이벤트명 (선택)</p>
          <input
            type="text"
            value={eventLabel}
            onChange={(e) => setEventLabel(e.target.value)}
            placeholder="예: OO기업 1차 면접"
            className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-white placeholder-purple-400/40 text-sm focus:outline-none focus:ring-1 focus:ring-primary-gold/50 focus:border-primary-gold/40 transition-all"
          />
        </div>
      </div>

      {/* Calculate button */}
      <button
        onClick={handleCalculate}
        disabled={!selectedEventType || isCalculating}
        className={`w-full py-3.5 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer ${
          selectedEventType && !isCalculating
            ? 'bg-gradient-to-r from-primary-gold to-amber-500 text-black hover:shadow-lg hover:shadow-amber-500/20 hover:scale-[1.01]'
            : 'bg-white/5 border border-white/10 text-purple-400/40 cursor-not-allowed'
        }`}
      >
        {isCalculating ? (
          <>
            <div className="w-4 h-4 flex items-center justify-center">
              <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
            </div>
            <span>길일 계산 중...</span>
          </>
        ) : (
          <>
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-sparkling-2-fill text-sm"></i>
            </div>
            <span>
              {selectedEventType
                ? `${MONTH_LABELS[selectedMonth - 1]} 개인 길일 찾기${eventLabel ? ' — ' + eventLabel : ''}`
                : '이벤트 유형을 먼저 선택해주세요'}
            </span>
          </>
        )}
      </button>

      {/* Results */}
      {results && results.length > 0 && (
        <div className="mt-6 space-y-3 animate-fadeIn">
          <div className="flex items-center justify-between mb-1">
            <p className="text-white font-bold text-sm">
              {MONTH_LABELS[selectedMonth - 1]} {currentEventConfig?.label} — 추천 길일
              {eventLabel && <span className="text-primary-gold ml-1 text-xs">({eventLabel})</span>}
            </p>
            <span className="text-purple-400/60 text-xs">{results.length}개 날짜</span>
          </div>

          {results.map((d, idx) => {
            const cfg = SCORE_CONFIG[d.score] ?? SCORE_CONFIG[3];
            const isTop = idx === 0;
            return (
              <div
                key={d.day}
                className={`flex items-start gap-4 p-4 rounded-2xl border transition-all ${
                  isTop ? `${cfg.bg} ring-1 ring-amber-500/30` : 'bg-white/3 border-white/8'
                }`}
              >
                <div className={`flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full ${
                  isTop ? 'bg-amber-500/30 border-2 border-amber-400/60' : 'bg-white/5 border border-white/10'
                }`}>
                  <span className={`text-xs font-black ${isTop ? 'text-amber-400' : 'text-purple-400/60'}`}>{idx + 1}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className={`font-black text-base ${isTop ? 'text-white' : 'text-purple-200'}`}>
                      {selectedMonth}월 {d.day}일 ({d.weekday}요일)
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold border ${cfg.bg} ${cfg.color}`}>{cfg.label}</span>
                    <span className="text-purple-400/50 text-xs">{d.elementOfDay}(日) 기운</span>
                  </div>
                  <p className={`text-xs leading-relaxed ${isTop ? 'text-purple-200/80' : 'text-purple-400/60'}`}>{d.advice}</p>
                </div>
                <div className="flex-shrink-0 flex flex-col items-end gap-1 w-12">
                  <span className={`text-sm font-black ${cfg.color}`}>{d.score}/5</span>
                  <div className="flex gap-0.5">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <div key={i} className={`w-1.5 h-5 rounded-sm ${i < d.score ? cfg.bar : 'bg-white/10'}`}></div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}

          <p className="text-purple-400/40 text-xs text-center pt-1">
            개인 사주 오행과 이벤트 기운을 종합 계산한 결과입니다. 시간도 맞추면 효과가 배가됩니다.
          </p>
        </div>
      )}

      {results && results.length === 0 && (
        <div className="mt-6 text-center py-8">
          <p className="text-purple-400/60 text-sm">선택하신 달에 적합한 길일을 찾지 못했습니다.</p>
          <p className="text-purple-400/40 text-xs mt-1">다른 달을 선택해보세요.</p>
        </div>
      )}
    </div>
  );
}
